import { ChevronDown, FileText, FolderArchive, Layers } from 'lucide-react';

import { formatarNumero } from '@/lib/formato';
import { Button } from '@/ui/primitives/button';
import { DropdownMenu, type ItemMenu } from '@/ui/primitives/dropdown-menu';
import { Dica } from '@/ui/primitives/tooltip';
import { type FormatoLote, LIMITE_LOTE_PDF, LIMITE_LOTE_XML } from '@fiscoai/contracts';

import { baixarLote, type FiltrosDocumentos } from './api';
import { useAcao } from './use-acao';

interface BaixarLoteProps {
  empresaIds: readonly string[];
  filtros: FiltrosDocumentos;
  /** Notas no filtro (do resumo); `undefined` enquanto carrega. */
  quantidade: number | undefined;
}

const OPCOES: { formato: FormatoLote; rotulo: string; limite: number; icone: typeof FileText }[] = [
  { formato: 'XML', rotulo: 'XMLs', limite: LIMITE_LOTE_XML, icone: FolderArchive },
  { formato: 'PDF', rotulo: 'PDFs (DANFE)', limite: LIMITE_LOTE_PDF, icone: FileText },
  { formato: 'XML_PDF', rotulo: 'XML + PDF', limite: LIMITE_LOTE_PDF, icone: Layers },
];

/**
 * "Baixar N notas (ZIP)" com os filtros da tela: XMLs para a contabilidade (spec 0008), PDFs
 * (DANFE) ou os dois (spec 0010). Cada formato tem o seu teto de notas por arquivo.
 */
export function BaixarLote({ empresaIds, filtros, quantidade }: BaixarLoteProps) {
  const acao = useAcao((formato: FormatoLote) => baixarLote(empresaIds, filtros, formato));
  const acima = quantidade !== undefined && quantidade > LIMITE_LOTE_XML;
  const rotulo =
    quantidade === undefined
      ? 'Baixar notas (ZIP)'
      : `Baixar ${formatarNumero(quantidade)} ${quantidade === 1 ? 'nota' : 'notas'} (ZIP)`;
  const gatilho = (
    <Button variante="secundario" carregando={acao.ocupado} disabled={!quantidade || acima}>
      <FolderArchive aria-hidden />
      {rotulo}
      <ChevronDown aria-hidden />
    </Button>
  );
  if (acima)
    return (
      <Dica texto={`Reduza o período: até ${formatarNumero(LIMITE_LOTE_XML)} notas por arquivo.`}>
        <span tabIndex={0}>{gatilho}</span>
      </Dica>
    );
  const itens: ItemMenu[] = OPCOES.map(({ formato, rotulo: nome, limite, icone: Icone }) => {
    const passou = (quantidade ?? 0) > limite;
    return {
      rotulo: nome,
      icone: <Icone aria-hidden />,
      aoSelecionar: () => {
        acao.disparar(formato);
      },
      desabilitado: passou,
      ...(passou ? { detalhe: `até ${formatarNumero(limite)} notas por arquivo` } : {}),
    };
  });
  return <DropdownMenu gatilho={gatilho} itens={itens} />;
}
